import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Alert, Box, Button, Card, CardContent, Container, Stack, Step, StepLabel, Stepper, Typography,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FormProvider from '../../components/form/FormProvider';
import { useSaveProgress } from '../../components/shared/SaveProgressOverlay';
import { useToast } from '../../components/shared/Toast';
import borrowerApplicationApi from '../../services/borrowerApplicationApi';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';
import PersonalInfoStep from './steps/PersonalInfoStep';
import EmploymentIncomeStep from './steps/EmploymentIncomeStep';
import AssetsStep from './steps/AssetsStep';
import LiabilitiesStep from './steps/LiabilitiesStep';
import PropertyStep from './steps/PropertyStep';
import LoanDetailsStep from './steps/LoanDetailsStep';
import ReviewDeclarationsStep from './steps/ReviewDeclarationsStep';
import {
  DEFAULT_APPLICATION_VALUES,
  STEP_LABELS,
  STEP_SCHEMAS,
  applyZodErrors,
} from './schemas';
import { mapApplicationPayload } from './mapApplicationPayload';

export const DRAFT_STORAGE_KEY = 'bp_application_draft';
const DRAFT_DEBOUNCE_MS = 600;

const STEPS = [
  PersonalInfoStep,
  EmploymentIncomeStep,
  AssetsStep,
  LiabilitiesStep,
  PropertyStep,
  LoanDetailsStep,
  ReviewDeclarationsStep,
];

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

const ApplyPage = () => {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { runWithSaveProgress, SaveProgressContainer } = useSaveProgress();
  const [submitApplication] = borrowerApplicationApi.endpoints.submitApplication.useMutation();

  const draft = useRef(loadDraft()).current;
  const [activeStep, setActiveStep] = useState(
    Number.isInteger(draft?.step) ? Math.min(Math.max(draft.step, 0), STEPS.length - 1) : 0,
  );
  const [submitError, setSubmitError] = useState(null);
  const draftTimer = useRef(null);

  const methods = useForm({
    defaultValues: {
      ...DEFAULT_APPLICATION_VALUES,
      ...(draft?.data || {}),
      personalInfo: { ...DEFAULT_APPLICATION_VALUES.personalInfo, ...(draft?.data?.personalInfo || {}) },
      employment: { ...DEFAULT_APPLICATION_VALUES.employment, ...(draft?.data?.employment || {}) },
      assets: { ...DEFAULT_APPLICATION_VALUES.assets, ...(draft?.data?.assets || {}) },
      liabilities: { ...DEFAULT_APPLICATION_VALUES.liabilities, ...(draft?.data?.liabilities || {}) },
      property: { ...DEFAULT_APPLICATION_VALUES.property, ...(draft?.data?.property || {}) },
      loanDetails: { ...DEFAULT_APPLICATION_VALUES.loanDetails, ...(draft?.data?.loanDetails || {}) },
      declarations: { ...DEFAULT_APPLICATION_VALUES.declarations, ...(draft?.data?.declarations || {}) },
    },
    mode: 'onBlur',
  });

  const { watch, getValues, setError, clearErrors, reset } = methods;

  // Debounced draft persistence
  useEffect(() => {
    const subscription = watch((values) => {
      if (draftTimer.current) clearTimeout(draftTimer.current);
      draftTimer.current = setTimeout(() => {
        try {
          localStorage.setItem(
            DRAFT_STORAGE_KEY,
            JSON.stringify({ data: values, step: activeStep, savedAt: Date.now() }),
          );
        } catch {
          // quota / private mode
        }
      }, DRAFT_DEBOUNCE_MS);
    });
    return () => {
      subscription.unsubscribe();
      if (draftTimer.current) clearTimeout(draftTimer.current);
    };
  }, [watch, activeStep]);

  // Persist step changes immediately
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      const existing = raw ? JSON.parse(raw) : { data: getValues() };
      localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify({ ...existing, step: activeStep, savedAt: Date.now() }),
      );
    } catch {
      // ignore
    }
  }, [activeStep, getValues]);

  const validateStep = useCallback(
    (stepIndex) => {
      clearErrors();
      const schema = STEP_SCHEMAS[stepIndex];
      const result = schema.safeParse(getValues());
      if (!result.success) {
        applyZodErrors(result, setError);
        return false;
      }
      return true;
    },
    [clearErrors, getValues, setError],
  );

  const handleNext = async () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleStartOver = () => {
    clearDraft();
    reset(DEFAULT_APPLICATION_VALUES);
    setActiveStep(0);
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;
    setSubmitError(null);

    const payload = mapApplicationPayload(getValues());

    try {
      const result = await runWithSaveProgress(
        () => submitApplication(payload).unwrap(),
        {
          headline: 'Submitting your application',
          body: 'Please wait while we send your application. Do not close this window.',
          messages: [
            'Sending your application…',
            'Creating your loan file…',
            'Almost done…',
          ],
        },
      );

      clearDraft();
      const applicationNumber =
        result?.applicationNumber || result?.loanNumber || result?.id || null;

      navigate('/apply/confirmation', {
        replace: true,
        state: { applicationNumber },
      });
    } catch (err) {
      const message = getAxiosErrorMessage(err, 'Unable to submit your application. Please try again.');
      setSubmitError(message);
      showToast(message, 'error');
    }
  };

  const StepComponent = STEPS[activeStep];
  const isLast = activeStep === STEPS.length - 1;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 } }}>
      {SaveProgressContainer}
      {ToastContainer}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h2" component="h1">
            Mortgage Application
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Step {activeStep + 1} of {STEPS.length} — {STEP_LABELS[activeStep]}
          </Typography>
        </Box>
        <Button
          variant="text"
          color="inherit"
          startIcon={<RestartAltIcon />}
          onClick={handleStartOver}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          Start over
        </Button>
      </Stack>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{ mb: 3, display: { xs: 'none', md: 'flex' } }}
      >
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <FormProvider methods={methods}>
            <StepComponent />

            {submitError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {submitError}
              </Alert>
            )}

            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={2}
              sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>
              {isLast ? (
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                  Submit application
                </Button>
              ) : (
                <Button variant="contained" color="primary" onClick={handleNext}>
                  Continue
                </Button>
              )}
            </Stack>
          </FormProvider>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ApplyPage;
